import Benefit, { BenefitTier } from "@/types/benefit"
import User from "@/types/user"
import { getBillingPeriodPerLabel } from "./products/product"
import { formatCurrency } from "./formatters"
import { TableColumns } from "@xy-planning-network/trees"

/**
 * getAdvSeasonStatus returns the status of two seasonal variables based on the current date.
 * "ADV Season" is roughly Jan 1st - March 31st of each year.
 *
 * inAdvSeason is true when the current date is inside the seasonal window.
 * afterAdvDeadline is true when the current date is inside the last month of the ADV season.
 *
 * @returns { inAdvSeason: boolean, advEndingSoon: boolean }
 */
export const getAdvSeasonStatus = () => {
  const now = new Date()
  const year = now.getFullYear()

  const start = new Date(`${year}-01-01T00:00:00`)
  const deadline = new Date(`${year}-03-01T00:00:00`)
  const end = new Date(`${year}-04-01T00:00:00`)

  return {
    inAdvSeason: now >= start && now < end,
    afterAdvDeadline: now >= deadline && now < end,
  }
}

/**
 * Get the available active SubscriptionItems of a benefit as an array of options
 * @param benefit Benefit
 * @returns {label: string, value: number}[]
 */
export function getBenefitActiveItemsOptions(benefit: Benefit) {
  if (!benefit.subscribed) {
    return []
  }

  return benefit.activeItems.map((subscriptionItem) => {
    return {
      help:
        subscriptionItem.productTier.shortDescription ||
        benefit.product.headline,
      item: subscriptionItem,
      label: `${subscriptionItem.productTier.name}`,
      sublabel: `${subscriptionItem.totalQuantity} Active Licenses`,
      value: subscriptionItem.id,
    }
  })
}

export interface BenefitTierOption {
  help: string
  label: string
  sublabel: string
  tier: BenefitTier
  value: number
}

/**
 * Get the available, displayable product tiers of a benefit as an array of options.
 * @param benefit Benefit
 * @returns BenefitTierOption[]
 */
export function getBenefitTierOptions(
  benefit: Benefit,
  includeAddOns = true
): BenefitTierOption[] {
  if (!benefit.subscribable) {
    return []
  }

  const subscribables = benefit.subscribables
    .filter((tier) => tier.show)
    .filter((tier) => {
      return tier.subscribable && includeAddOns ? true : !tier.addon
    })

  return subscribables.map((tier) => {
    let priceDisplay = formatCurrency(tier.memberPrice)
    const billingPeriod = getBillingPeriodPerLabel(tier.billingPeriod)

    priceDisplay =
      billingPeriod !== "" ? `${priceDisplay}/${billingPeriod}` : priceDisplay

    return {
      help: tier.shortDescription || benefit.product.headline,
      label: tier.name,
      sublabel: priceDisplay,
      tier,
      value: tier.id,
    }
  })
}

/**
 * Get the common set of data properties for a benefit payload
 * @param user User
 * @returns Object
 */
export function getCommonBenefitData(user: User = null) {
  if (user === null) {
    return {
      firstName: "",
      lastName: "",
      email: "",
      firmName: "",
    }
  }

  return {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    firmName: user?.firm?.name || "",
  }
}

/**
 * Get the common set of data properties for a cancel with vendor user id payload
 * @param user User
 * @returns Object
 */
export function getCancelWithVendorId(user: User = null) {
  if (user === null) {
    return {
      firstName: "",
      lastName: "",
      email: "",
      userId: "",
    }
  }

  return {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    userId: "",
  }
}

/**
 * getLicenseTableData structures the Benefit and currentUser as a table for displaying
 * information about licenses.
 */
export function getLicenseTableData(benefit: Benefit) {
  const items = benefit.subscribables.reduce(
    (map, productTier) => {
      map[productTier.id] = {
        name: productTier.name,
        numActive: 0,
        numPendingSignup: 0,
        numPendingCancel: 0,
        show: productTier.show,
      }

      return map
    },
    {} as Record<
      number,
      {
        name?: string
        numActive: number
        numPendingSignup: number
        numPendingCancel: number
        show: boolean
      }
    >
  )

  if (benefit.activeItems) {
    benefit.activeItems.forEach((subscriptionItem) => {
      items[subscriptionItem.productTierId].numActive =
        subscriptionItem.totalQuantity
    })
  }

  if (benefit.pendingDecrements) {
    benefit.pendingDecrements.forEach((productRequest) => {
      productRequest.subscriptionItems.forEach((subscriptionItem) => {
        items[subscriptionItem.productTierId].numPendingCancel++
      })
    })
  }

  // NOTE(dlk): This is an edge case, when a cancel is active, the table isn't displayed.
  //
  // In any case, there can be pending decrements alongside one cancel for a ProductTier.
  // However, if the cancel is approved, then the decrements are moot
  // and the user would have no licenses as a result of the cancellation.
  if (benefit.pendingCancels) {
    benefit.pendingCancels.forEach((productRequest) => {
      productRequest.subscriptionItems.forEach((subscriptionItem) => {
        items[subscriptionItem.productTierId].numPendingCancel =
          items[subscriptionItem.productTierId].numActive
      })
    })
  }

  if (benefit.pendingIncrements) {
    benefit.pendingIncrements.forEach((productRequest) => {
      productRequest.productTiers.forEach((productTier) => {
        items[productTier.id].numPendingSignup++
      })
    })
  }

  // NOTE(dlk): This is an edge case.
  // There ought to be no pending signups alongside other pending requests.
  if (benefit.pendingSignups) {
    benefit.pendingSignups.forEach((productRequest) => {
      productRequest.productTiers.forEach((productTier) => {
        items[productTier.id].numPendingSignup = 1
      })
    })
  }

  const columns: TableColumns<(typeof items)[number]> = [
    { title: "Active Licenses", render: "numActive" },
    // NOTE(dlk): these are called "signups" in order to keep the display simple for the user.
    { title: "Pending Licenses", render: "numPendingSignup" },
    { title: "Pending Cancellations", render: "numPendingCancel" },
  ]

  const data = Object.values(items).filter((i) => i.show)

  if (benefit.subscribables && benefit.subscribables.length > 1)
    columns.push({ title: "", render: "name" })

  return {
    tableColumns: columns,
    tableData: data,
  }
}

/**
 * Get an array of all benefit images in the assets directory
 * [docs](https://vitejs.dev/guide/features.html#glob-import-as)
 * @returns string[]
 */
export function useBenefitImage() {
  const benefitImages = import.meta.glob<string>(
    "../assets/member-benefits/*.svg",
    {
      eager: true,
      import: "default",
      query: "?url",
    }
  )
  const benefitImagePaths: Record<string, string> = {}

  for (const image in benefitImages) {
    benefitImagePaths[
      image.replace("../assets/member-benefits/", "").replace(".svg", "")
    ] = benefitImages[image]
  }

  return {
    benefitImages: benefitImagePaths,
    getBenefitImage(benefit: Benefit) {
      return benefitImagePaths[benefit.product.slug] !== undefined
        ? benefitImagePaths[benefit.product.slug]
        : ""
    },
  }
}

/**
 * Determine if a benefit is recommended based on a given firm phase.
 * @param benefit Benefit
 * @param phase FirmPhase | string
 * @returns boolean
 */
export function isRecommended(benefit: Benefit, phase = "") {
  if (phase === "") {
    return true
  }

  return benefit.product.firmPhases.reduce((found, firmPhase) => {
    if (firmPhase.phase === phase) {
      found = true
    }
    return found
  }, false)
}

export function redirectNext(url: string) {
  /**
   * if the signup success returns a next url, go ahead and send the user there
   *
   * NOTE(spk): window.open($url, "_blank") can be blocked by a
   * browsers pop-up blocking feature when the origins do not match.
   * Instead, we can use a link element to open the URL in a new tab.
   */
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("target", "_blank")
  link.click()
}
